import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import {
  useWashroomAuditStore,
  usePremisesStore,
  useOrgUsersStore,
  WashroomAuditType,
} from "../../../store/store";
import { PageLoader } from "../../../components/loader/Loader";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { themeQuartz } from "ag-grid-community";
import Modal from "../../../components/modal/Modal";
import images from "../../../assets/images";
import axiosInstance from "../../../common/axiosInstance";
import { toast } from "react-toastify";
import { useAuth } from "../../../auth/AuthProvider";
import { WASHROOM_AUDITS } from "../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermission";
import { getAllTypeList } from "../../../service/api";
import { WashroomTypeListType } from "../../../types/api";

interface Washroom {
  _id: string;
  premisesId: string;
  washroomName: string;
  type: string;
  floor: string;
  assignedStaff: string;
  createdDate: string;
  tenantId: string;
  createdAt: string;
}

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  tenantId: string;
}

interface Premise {
  id: string;
  premisesName: string;
}

interface Audit {
  _id: string;
  washroomId: string;
  checkListId: string;
  premisesId: string;
  auditDate: string;
  status: "scheduled" | "pending" | "completed";
  rating: number;
  assignedTo: string;
  frequency: string;
  remarks?: string;
  images?: string[];
  tenantId: string;
  createdBy: string;
}

interface AuditFormData {
  premiseId: string;
  washroomId: string;
  checkListId: string;
  auditDate: string;
  assignedTo: string;
  frequency: string;
}

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const AuditList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading, error, fetchAudits, audits, createAudit, updateAudit } =
    useWashroomAuditStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const [washrooms, setWashrooms] = useState<Washroom[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentAuditId, setCurrentAuditId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AuditFormData>({
    premiseId: "",
    washroomId: "",
    checkListId: "",
    auditDate: "",
    assignedTo: "",
    frequency: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const [filteredStaff, setFilteredStaff] = useState<any[]>([]);
  const [auditList, setAuditList] = useState<WashroomAuditType[]>([]);
  const [washroomTypeList, setWashroomTypeList] = useState<
    WashroomTypeListType[]
  >([]);
  const { canCreate, canUpdate } = usePermissions();
  const isAdmin =
    user?.roles.includes("orgadmin") || user?.roles.includes("superadmin");

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchAudits(),
          fetchPremises(),
          fetchOrgUsers(),
          fetchWashrooms(),
          fetchChecklists(),
        ]);
      } catch (err) {
        console.error("Error fetching initial data:", err);
        toast.error("Failed to fetch initial data");
      }
    };
    fetchData();
  }, [fetchAudits, fetchPremises, fetchOrgUsers]);

  useEffect(() => {
    if (audits.length > 0) {
      setAuditList(audits.filter((audit) => audit.status !== "completed"));
    }
  }, [audits]);

  useEffect(() => {
    if (!formData.premiseId || !orgUsersData.length) {
      setFilteredStaff([]);
      // Only clear assignedTo if we're not in edit mode
      if (!isEditMode) {
        setFormData((prev) => ({ ...prev, assignedTo: "" }));
      }
      return;
    }

    const staffForPremises = orgUsersData.filter((user: any) => {
      if (!user.premise) return false;
      const userPremises = Array.isArray(user.premise)
        ? user.premise
        : [user.premise];
      return userPremises.includes(formData.premiseId);
    });

    setFilteredStaff(staffForPremises);

    // Only clear assignedTo if the current assigned person is not in the filtered list
    // and we're not in edit mode (to preserve the assigned person during edit)
    if (
      formData.assignedTo &&
      !staffForPremises.some((u: any) => u.id === formData.assignedTo) &&
      !isEditMode
    ) {
      setFormData((prev) => ({ ...prev, assignedTo: "" }));
    }
  }, [formData.premiseId, orgUsersData, isEditMode]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await getAllTypeList();
        if (res.data?.data && Array.isArray(res.data.data)) {
          setWashroomTypeList(res.data.data);
        } else {
          setWashroomTypeList([]);
        }
      } catch (error) {
        console.error("Failed to fetch washroom types:", error);
      }
    };
    fetchTypes();
  }, []);

  const fetchWashrooms = async () => {
    try {
      const response = await axiosInstance.get(`/api/washroom`);
      setWashrooms(response.data.message || []);
    } catch (err) {
      console.error("Error fetching washrooms:", err);
      toast.error("Failed to fetch washroom data");
    }
  };

  const fetchChecklists = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/washroom-checklist-config`
      );
      if (response.status === 200) {
        setChecklists(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching checklists:", error);
      toast.error("Failed to fetch checklists");
    }
  };

  const filteredWashrooms = washrooms.filter(
    (w) => w.premisesId === formData.premiseId,
  );
  const filteredChecklists = checklists.filter(
    (c) => c.premisesId === formData.premiseId,
  );

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.premiseId) errors.premiseId = "Premise is required";
    if (!formData.washroomId) errors.washroomId = "Washroom is required";
    if (!formData.checkListId) errors.checkListId = "Checklist is required";
    if (!formData.auditDate) errors.auditDate = "Audit date is required";
    if (!formData.assignedTo) errors.assignedTo = "Assigned to is required";
    if (!formData.frequency) errors.frequency = "Frequency is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    const auditData = {
      premisesId: formData.premiseId,
      washroomId: formData.washroomId,
      checkListId: formData.checkListId,
      auditDate: formData.auditDate,
      assignedTo: formData.assignedTo,
      frequency: formData.frequency,
    };

    try {
      if (isEditMode && currentAuditId) {
        await updateAudit(currentAuditId, auditData);
      } else {
        await createAudit(auditData);
      }

      setModalOpen(false);
      await fetchAudits();
      resetForm();
    } catch (error) {
      console.error("Error saving audit:", error);
    }
  };

  const handleEdit = (audit: Audit) => {
    const isAssigned = user?.id === audit?.assignedTo;
    // const canEdit = isAssigned && !isAdmin;

    if (isAdmin) {
      setIsEditMode(true);
      setCurrentAuditId(audit._id);
      setFormData({
        premiseId: audit.premisesId,
        washroomId: audit.washroomId,
        checkListId: audit.checkListId,
        auditDate: audit.auditDate,
        assignedTo: audit.assignedTo,
        frequency: audit.frequency,
      });
      setModalOpen(true);
    } else if (isAssigned) {
      navigate(`/washroom-management/audits/view/${audit._id}`);
    } else {
      navigate(`/washroom-management/audits/view/${audit._id}`);
    }
  };

  const resetForm = () => {
    setIsEditMode(false);
    setCurrentAuditId(null);
    setFormData({
      premiseId: "",
      washroomId: "",
      checkListId: "",
      auditDate: "",
      assignedTo: "",
      frequency: "",
    });
    setFormErrors({});
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "premiseId"
        ? { washroomId: "", checkListId: "", assignedTo: "" }
        : {}),
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Washroom Name",
        field: "washroomId",
        flex: 1,
        sortable: true,
        valueGetter: (params) => {
          const washroom = params.data.washroomDetails.washroomName;
          return washroom ? washroom : "N/A";
        },
      },
      {
        headerName: "Washroom Type",
        field: "washroomId",
        flex: 1,
        valueGetter: (params) => {
          const washroom = washroomTypeList.find(
            (type) => type._id === params.data?.washroomDetails.type,
          );
          return washroom ? washroom.typeName : "N/A";
        },
      },
      {
        headerName: "Floor",
        field: "washroomId",
        flex: 0.5,
        valueGetter: (params) => {
          const washroom = params.data.washroomDetails.floor;
          return washroom ? washroom : "N/A";
        },
      },
      {
        headerName: "Checklist",
        field: "checkListId",
        flex: 0.8,
        valueGetter: (params) => {
          const checklist = checklists.find(
            (c) => c._id === params.data.checkListId,
          );
          return checklist ? checklist.name : params.data.checkListId;
        },
      },
      {
        headerName: "Premises",
        field: "premisesId",
        flex: 0.8,
        valueGetter: (params) => {
          const premise = allPremises.find(
            (p) => p.id === params.data.premisesId,
          );
          return premise ? premise.premisesName : params.data.premisesId;
        },
      },
      {
        headerName: "Audit Date",
        field: "auditDate",
        flex: 0.7,
        sortable: true,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return params.value;
          return date.toLocaleDateString();
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 0.8,
        cellRenderer: (params: any) => {
          const status = params.value?.toLowerCase();
          return (
            <div
              className={`px-2 py-1 rounded-full text-center text-sm ${status === "completed"
                  ? "bg-green-100 text-green-800"
                  : status === "scheduled"
                    ? "bg-blue-100 text-yellow-800"
                    : status === "pending"
                      ? "bg-yellow-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                }`}
            >
              {params.value}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        flex: 0.5,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-1">
            <button
              className="p-1 hover:bg-red-100 rounded"
              onClick={() =>
                navigate(`/washroom-management/audits/view/${params.data._id}`)
              }
            >
              <img
                className="iconTd w-5 h-5"
                src={images.viewIcon || "/placeholder.svg"}
                alt="View"
              />
            </button>
            {params.data.status !== "completed" &&
              canUpdate(WASHROOM_AUDITS) && (
                <button
                  className="p-1 hover:bg-green-100 rounded"
                  onClick={() => handleEdit(params.data)}
                >
                  <img
                    className="iconTd w-5 h-5"
                    src={images.editIcon || "/placeholder.svg"}
                    alt="Edit"
                  />
                </button>
              )}
          </div>
        ),
      },
    ],
    [navigate, checklists, allPremises, canUpdate],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      flex: 1,
    }),
    [],
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen mt-14 ml-2 min-h-100vh">
        <PageLoader />
      </div>
    );
  }

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-100vh">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Washroom Audits</h1>
          <div className="flex gap-2">
            <button
              className="flex items-center justify-center font-semibold gap-2 bg-white text-[#0fa9a6] !border !border-[#0fa9a6] px-4 py-2 rounded-full hover:!bg-[#0fa9a6] hover:!border-teal-700 hover:text-white transition"
              onClick={() => navigate("/washroom-management/audits/history")}
            >
              AUDIT HISTORY
            </button>
            {canCreate(WASHROOM_AUDITS) && (
              <button
                className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
                onClick={() => setModalOpen(true)}
              >
                CREATE AUDIT
              </button>
            )}
          </div>
        </div>

        <div className="ag-theme-alpine w-full h-[calc(100vh-200px)]">
          <AgGridReact
            rowData={auditList}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            headerHeight={65}
            rowHeight={50}
            pagination={true}
            paginationPageSize={20}
            enableCellTextSelection={true}
            suppressCellFocus={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        closeButton={false}
        modalClass={"bg-white w-full max-w-4xl rounded-xl shadow-lg relative"}
      >
        <div className="pt-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            {isEditMode ? "Update Audit" : "Create New Audit"}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-3 gap-4 px-6 pb-6">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Premises
                </label>
                <select
                  name="premiseId"
                  value={formData.premiseId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.premiseId ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                >
                  <option value="">Select Premises</option>
                  {allPremises.length > 0 ? (
                    allPremises.map((premise) => (
                      <option key={premise.id} value={premise.id}>
                        {premise.premisesName}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No premises available
                    </option>
                  )}
                </select>
                {formErrors.premiseId && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.premiseId}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Washroom Name
                </label>
                <select
                  name="washroomId"
                  value={formData.washroomId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.washroomId ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  disabled={!formData.premiseId}
                >
                  <option value="">
                    {!formData.premiseId
                      ? "Select Premises"
                      : "Select Washroom Name"}
                  </option>
                  {filteredWashrooms.length > 0 ? (
                    filteredWashrooms.map((washroom) => (
                      <option key={washroom._id} value={washroom._id}>
                        {washroom.washroomName}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No washrooms available for selected premises
                    </option>
                  )}
                </select>
                {formErrors.washroomId && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.washroomId}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Checklist Name
                </label>
                <select
                  name="checkListId"
                  value={formData.checkListId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.checkListId
                      ? "border-red-500"
                      : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  disabled={!formData.premiseId}
                >
                  <option value="">
                    {!formData.premiseId
                      ? "Select Premises"
                      : "Select Checklist Name"}
                  </option>
                  {filteredChecklists.length > 0 ? (
                    filteredChecklists.map((checklist) => (
                      <option key={checklist._id} value={checklist._id}>
                        {checklist.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No checklists available for selected premises
                    </option>
                  )}
                </select>
                {formErrors.checkListId && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.checkListId}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Audit Date
                </label>
                <input
                  type="date"
                  name="auditDate"
                  value={formData.auditDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.auditDate ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                />
                {formErrors.auditDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.auditDate}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.assignedTo ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  disabled={!formData.premiseId}
                >
                  <option value="">Select Staff</option>
                  {filteredStaff.length === 0 ? (
                    <option value="" disabled>
                      No staff available for selected premises
                    </option>
                  ) : (
                    filteredStaff.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))
                  )}
                </select>
                {formErrors.assignedTo && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.assignedTo}
                  </p>
                )}
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Frequency
                </label>
                <select
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${formErrors.frequency ? "border-red-500" : "border-gray-300"
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                >
                  <option value="">Select Frequency</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Yearly">Yearly</option>
                </select>
                {formErrors.frequency && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.frequency}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
              <button
                type="button"
                className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600"
                onClick={() => {
                  setModalOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600"
              >
                {isEditMode ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default AuditList;
